import { createSelector } from '@reduxjs/toolkit';

import { Condition } from 'editor/src/store/design/types';
import { RootState } from 'editor/src/store/index';

// Most designs have no conditions at all; one shared array for that case keeps
// the empty result referentially stable. See getGlobalConditionsFromOtherSpreads
// for the same trick and the same reason.
const NONE: Condition[] = [];

const selectSpreads = (state: RootState) => state.design.designData?.spreads;

/**
 * Every condition in the design, in spread order.
 *
 * Design-wide on purpose: a label on the back of a card can vary with a
 * picklist stored on the front, so a panel offering "vary by picklist" cannot
 * limit itself to the current spread.
 *
 * Select this with `shallowEqual`. The result is rebuilt whenever anything in
 * `spreads` changes — an unrelated text edit included — but immer leaves an
 * untouched `conditions` entry alone, so the MEMBERS stay reference-equal and
 * `shallowEqual` hands the caller back its previous array. Without that, every
 * text panel in the list would re-render on every keystroke.
 */
const getDesignConditions = createSelector([selectSpreads], (spreads): Condition[] => {
  const conditions = (spreads ?? []).flatMap((spread) => Object.values(spread.conditionGroup?.conditions ?? {}));
  return conditions.length ? conditions : NONE;
});

export default getDesignConditions;
